import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Avatar,
  Chip,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  alpha,
  Stack,
  CircularProgress,
  Tooltip,
  Fade,
  Zoom,
  Button,
  Popover,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  ChatBubbleOutline as ChatIcon,
  Circle as CircleIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  AttachFile as AttachFileIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { formatDate } from "../../../../../utils/dateUtils";
import {
  AnnotationCommentEntityType,
  CommentModuleTypeEnum,
} from "../../../../../utils/enums";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_CONTROL_COMMENTS,
  CREATE_COMMENT,
  UPDATE_COMMENT,
  DELETE_COMMENT,
} from "../../../../../apis/settingsApis/audit-apis/commentApis";
import { GET_USER_DETAILS } from "../../../../../apis/dashboard-apis/dashboardDetailApi";
import ConfirmDialog from "../../../../../components/ConfirmDialog/ConfirmDialog";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";
import { GET_EXTERNAL_USERS } from "../../../../../apis/organization-apis/people-apis/externalUserApis";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import { useAuth0 } from "@auth0/auth0-react";
import { uploadCommentAttachment } from "../../../../../apis/settingsApis/audit-apis/commentAttachmentApis";
import CommentAttachments from "./CommentAttachments";
import Role from "../../../../../utils/roles";
import { firstActiveAuditorRowPerId } from "../../utils/auditDetailsUtils";
const ControlCommentsDrawer = ({
  open,
  onClose,
  control,
  audit,
  isAuditorView = false,
}) => {
  const theme = useTheme();
  const commentsEndRef = useRef(null);
  const textInputRef = useRef(null);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCommentForDelete, setSelectedCommentForDelete] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedCommentForMenu, setSelectedCommentForMenu] = useState(null);
  const attachmentFileInputRef = useRef(null);
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();

  const { data: employeesData } = useQuery(GET_STAFF_DETAILS, {
    variables: {
      filter: { in_scope: true },
      audit_id: audit?.id,
    },
    skip: !open,
    fetchPolicy: "cache-first",
  });

  const { data: externalUsersData } = useQuery(GET_EXTERNAL_USERS, {
    variables: {
      page: 1,
      limit: 1000,
    },
    skip: !open,
    fetchPolicy: "cache-first",
  });

  const mentionOptions = useMemo(() => {
    // Use auditors array directly from audit object (new schema)
    const assignedAuditorIds = new Set(
      (audit?.auditors || []).map((a) => String(a.id))
    );

    const employees = (employeesData?.getEmployees?.data || [])
      .filter((emp) => {
        return emp.roles?.some(
          (role) => role.role_description?.toLowerCase() === Role.ADMIN
        );
      })
      .map((emp) => ({
        id: emp.employee_data_id,
        name: emp.name || emp.work_email?.split("@")[0] || "Unknown",
        email: emp.work_email,
        type: "admin",
        displayName: `@${emp.name || emp.work_email?.split("@")[0] || "Unknown"}`,
      }));

    const auditors = firstActiveAuditorRowPerId(
      externalUsersData?.getExternalUsers?.data || []
    )
      .filter((user) => assignedAuditorIds.size > 0 && assignedAuditorIds.has(String(user.id)))
      .map((user) => ({
        id: user.id,
        name: user.name || user.email?.split("@")[0] || "Unknown",
        email: user.email,
        type: "auditor",
        displayName: `@${user.name || user.email?.split("@")[0] || "Unknown"}`,
      }));

    return [...employees, ...auditors];
  }, [employeesData, externalUsersData, audit?.auditors]);

  const filteredMentions = useMemo(() => {
    if (!mentionQuery) return mentionOptions;
    
    const query = mentionQuery.toLowerCase();
    return mentionOptions
      .filter(
        (option) =>
          option.name.toLowerCase().includes(query) ||
          option.email.toLowerCase().includes(query)
      );
  }, [mentionOptions, mentionQuery]);

  const getValidMentionsFromText = (text, mentions) => {
    if (!text || !mentions?.length) return [];
    return mentions.filter((mention) => {
      const escapedDisplayName = mention.displayName.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );
      const mentionRegex = new RegExp(
        `(^|\\s)${escapedDisplayName}(?=\\s|$)`,
        "g"
      );
      return mentionRegex.test(text);
    });
  };




  const { data: userData } = useQuery(GET_USER_DETAILS);
  const userDetails = userData?.getAccountDetails?.userDetails || {};

  const {
    data: commentsData,
    loading: commentsLoading,
    refetch: refetchComments,
  } = useQuery(GET_CONTROL_COMMENTS, {
    variables: {
      filter: {
        module_type: CommentModuleTypeEnum.AUDIT,
        module_id: audit?.id,
        entity_type: AnnotationCommentEntityType.CONTROL,
        entity_id: control?.auditControlId,
      },
      page: 1,
      limit: 100,
    },
    skip: !open || !control?.auditControlId || !audit?.id,
    fetchPolicy: "cache-and-network",
  });

  const [createCommentMutation] = useMutation(CREATE_COMMENT, {
    onCompleted: () => {
      refetchComments();
      setNewComment("");
      setSelectedMentions([]);
      setSelectedAttachments([]);
      closeMentionPopover();
      setLoading(false);
    },
    onError: (error) => {
      console.error("Error creating comment:", error);
      setLoading(false);
    },
  });
  const [updateCommentMutation] = useMutation(UPDATE_COMMENT);
  const [deleteCommentMutation] = useMutation(DELETE_COMMENT);

  const comments = commentsData?.getAnnotationComments?.data || [];

  useEffect(() => {
    if (commentsEndRef.current && comments.length > 0) {
      commentsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [comments.length]);

  useEffect(() => {
    if (!open) {
      closeMentionPopover();
    }
  }, [open]);

  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    setNewComment(value);
    setSelectedMentions((prevMentions) =>
      getValidMentionsFromText(value, prevMentions)
    );
    
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(" ") && !textAfterAt.includes("\n")) {
        const query = textAfterAt.toLowerCase();
        setMentionQuery(query);
        setMentionStartIndex(lastAtIndex);
        setMentionSelectedIndex(0);
        
        if (textInputRef.current) {
          setMentionAnchor(textInputRef.current);
        }
      } else {
        closeMentionPopover();
      }
    } else {
      closeMentionPopover();
    }
  };

  const handleCommentKeyDown = (e) => {
    if (mentionAnchor && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionSelectedIndex((prev) =>
          prev < filteredMentions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey) {
        e.preventDefault();
        selectMention(filteredMentions[mentionSelectedIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeMentionPopover();
      }
      return;
    }
    
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  const selectMention = (mention) => {
    if (!mention) return;
    
    const textBefore = newComment.substring(0, mentionStartIndex);
    const textAfter = newComment.substring(mentionStartIndex);
    const spaceIndex = textAfter.indexOf(" ");
    const newlineIndex = textAfter.indexOf("\n");
    const endIndex = spaceIndex !== -1 && newlineIndex !== -1
      ? Math.min(spaceIndex, newlineIndex)
      : spaceIndex !== -1
      ? spaceIndex
      : newlineIndex !== -1
      ? newlineIndex
      : textAfter.length;
    
    const textAfterMention = textAfter.substring(endIndex);
    
    const newText = `${textBefore}${mention.displayName} ${textAfterMention}`;
    setNewComment(newText);
    
    const existingMention = selectedMentions.find((m) => m.id === mention.id);
    if (!existingMention) {
      setSelectedMentions([...selectedMentions, mention]);
    }
    
    closeMentionPopover();
    
    setTimeout(() => {
      if (textInputRef.current) {
        const textarea = textInputRef.current.querySelector('textarea');
        if (textarea) {
          const newCursorPos = mentionStartIndex + mention.displayName.length + 1;
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }
      }
    }, 0);
  };

  const closeMentionPopover = () => {
    setMentionAnchor(null);
    setMentionQuery("");
    setMentionStartIndex(-1);
    setMentionSelectedIndex(0);
  };

  const handleFileSelection = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }

    setUploadingAttachments(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;

      const uploadedAttachments = [];

      for (const file of files) {
        const attachment = await uploadCommentAttachment(file, accessToken, idToken);
        uploadedAttachments.push(attachment);
      }

      setSelectedAttachments((prev) => [...prev, ...uploadedAttachments]);

      if (attachmentFileInputRef.current) {
        attachmentFileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading attachments:", error);
      setSnackbarInfo({
        open: true,
        message: "Failed to upload attachment",
        severity: "error",
      });
    } finally {
      setUploadingAttachments(false);
    }
  };

  const handleSubmitComment = async () => {
    if ((!newComment.trim() && selectedAttachments.length === 0) || !userDetails?.user_id) return;
    
    setLoading(true);
    try {
        const validMentions = getValidMentionsFromText(newComment, selectedMentions);
        const mentioned_peoples = validMentions
          .filter((mention) => mention.id) 
          .map((mention) => ({
            authorId: mention.id,
            authorType: mention.type, 
          }))

    
        await createCommentMutation({
            variables: {
            input: {
                module_type: CommentModuleTypeEnum.AUDIT,
                module_id: audit?.id,
                entity_type: AnnotationCommentEntityType.CONTROL,
                entity_id: control?.auditControlId,
                content: newComment.trim() || "",
                is_internal: false,
                mentioned_peoples: validMentions.length > 0 ? mentioned_peoples : undefined,
                attachment_ids: selectedAttachments.length > 0 ? selectedAttachments.map(a => a.id) : undefined,
            },
            },
        });
    } catch (error) {
      console.error("Error creating comment:", error);
      setLoading(false);
    }
  };

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";

  const isAuditorComment = (comment) =>
    comment.author_type === "auditor";

  const isCommentOwner = (comment) => {
    const currentUserId = userDetails?.employee_data_id || userDetails?.user_id;
    return String(comment?.author_id) === String(currentUserId);
  };

  const handleEditClick = (comment) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content || "");
    setMenuAnchor(null);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentContent("");
  };

  const handleSaveEdit = async (comment) => {
    const content = editCommentContent.trim();
    if (!content || !comment?.id) return;

    setIsUpdatingComment(true);
    try {
      await updateCommentMutation({
        variables: {
          input: [
            {
              id: comment.id,
              module_id: audit?.id,
              content,
              is_internal: !!comment.is_internal,
            },
          ],
        },
      });

      await refetchComments();
      handleCancelEdit();
      setSnackbarInfo({
        message: "Comment updated successfully.",
        vertical: "top",
        horizontal: "right",
      });
    } catch (error) {
      console.error("Error updating comment:", error);
      setSnackbarInfo({
        message: "Failed to update comment.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setIsUpdatingComment(false);
    }
  };

  const handleOpenDeleteDialog = (comment) => {
    setSelectedCommentForDelete(comment);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeletingComment) return;
    setDeleteDialogOpen(false);
    setSelectedCommentForDelete(null);
  };

  const handleConfirmDelete = async () => {
    const commentId = selectedCommentForDelete?.id;
    if (!commentId) return;

    setIsDeletingComment(true);
    try {
      await deleteCommentMutation({
        variables: {
          input: [
            {
              id: commentId,
              module_id: audit?.id,
              deleted_at: new Date().toISOString(),
            },
          ],
        },
      });

      await refetchComments();

      if (editingCommentId === commentId) {
        handleCancelEdit();
      }
      handleCloseDeleteDialog();
      setSnackbarInfo({
        message: "Comment deleted successfully.",
        vertical: "top",
        horizontal: "right",
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
      setSnackbarInfo({
        message: "Failed to delete comment.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setIsDeletingComment(false);
    }
  };

  const handleRefreshComments = async () => {
    setIsRefreshing(true);
    try {
      await refetchComments();
    } catch (error) {
      console.error("Error refreshing comments:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleMenuOpen = (event, comment) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCommentForMenu(comment);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCommentForMenu(null);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 500 },
          bgcolor: "#FAFBFC",
          boxShadow: "none",
          borderLeft: "1px solid #E8EBF0",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* ─── Header ─── */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #EBF4FF 0%, #F8FBFF 100%)",
            borderBottom: "1px solid #E1ECFA",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5" sx={{ fontSize: "1.05rem" }}>
                  Comments
                </Typography>
                {comments.length > 0 && (
                  <Zoom in={true}>
                    <Box
                      sx={{
                        bgcolor: alpha("#5B8AC6", 0.15),
                        color: "#5B8AC6",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        minWidth: 22,
                        height: 22,
                        borderRadius: "11px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 0.75,
                        boxShadow: `0 2px 8px ${alpha("#5B8AC6", 0.2)}`,
                      }}
                    >
                      {comments.length}
                    </Box>
                  </Zoom>
                )}
              </Box>
              {(control?.controlsName || control?.controlsTitle) && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    display: "block",
                    maxWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "#5B8AC6",
                    mt: 0.25,
                  }}
                >
                  {control?.controlsName && control?.controlsTitle
                    ? `${control.controlsName} : ${control.controlsTitle}`
                    : control?.controlsName || control?.controlsTitle}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Refresh comments">
              <IconButton
                onClick={handleRefreshComments}
                size="small"
                disabled={isRefreshing}
                sx={{
                  color: "#8FA5C0",
                  transition: "all 0.2s ease",
                  animation: isRefreshing ? "spin 0.8s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                  "&:hover": {
                    color: "#5B8AC6",
                    bgcolor: "rgba(91, 138, 198, 0.08)",
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "#8FA5C0",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#5B8AC6",
                  bgcolor: "rgba(91, 138, 198, 0.08)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ─── Messages ─── */}
        <Box
          variant="scrollable"
          sx={{
            flex: 1,
            px: 2.5,
            py: 3,
            bgcolor: "#FAFBFC",
            overflowY: "auto",
          }}
        >
          {commentsLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <CircularProgress 
                size={32} 
                thickness={3.5}
                sx={{ color: "#4A90E2" }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#8FA5C0",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                }}
              >
                Loading comments...
              </Typography>
            </Box>
          ) : comments.length === 0 ? (
            <Fade in={true} timeout={600}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #EBF4FF 0%, #D9EBFF 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <ChatIcon sx={{ fontSize: 36, color: "#4A90E2" }} />
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  No comments yet
                </Typography>
                <Typography variant="body2" sx={{ maxWidth: 260, color: theme.customColors.text.secondary }}>
                  Start the conversation between admins and auditors
                </Typography>
              </Box>
            </Fade>
          ) : (
            <Stack spacing={2}>
              {comments.map((comment, index) => {
                const auditor = isAuditorComment(comment);
                const isOwner = isCommentOwner(comment);
                const isEditing = editingCommentId === comment.id;

                return (
                  <Fade in={true} key={comment.id} timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isOwner ? "row-reverse" : "row",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Avatar */}
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          background: auditor
                            ? "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)"
                            : theme.customColors.status.info.main,
                          color: "#fff",
                          boxShadow: auditor
                            ? "0 2px 8px rgba(74, 144, 226, 0.25)"
                            : `0 2px 8px ${alpha(theme.customColors.status.info.main, 0.25)}`,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(comment.author_name)}
                      </Avatar>

                      {/* Message Container */}
                      <Box
                        sx={{
                          maxWidth: "75%",
                          minWidth: "200px",
                        }}
                      >
                        {/* Name and Time */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                            flexDirection: isOwner ? "row-reverse" : "row",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#2C4A68",
                            }}
                          >
                            {comment.author_name}
                          </Typography>
                          <Chip
                            label={auditor ? "Auditor" : "Admin"}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              bgcolor: auditor ? "#EBF4FF" : theme.customColors.status.info.bg,
                              color: auditor ? "#4A90E2" : theme.customColors.status.info.text,
                              border: "none",
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              color: "#8FA5C0",
                            }}
                          >
                            {formatDate(comment.created_at) || "Just now"}
                          </Typography>
                        </Box>

                        {/* Message Bubble */}
                        <Box
                          sx={{
                            position: "relative",
                            bgcolor: isOwner ? "#4A90E2" : "#FFFFFF",
                            borderRadius: 2.5,
                            px: 2,
                            py: 1.5,
                            border: isOwner ? "none" : "1px solid #E8EBF0",
                            boxShadow: isOwner
                              ? "0 2px 8px rgba(74, 144, 226, 0.2)"
                              : "0 1px 3px rgba(0, 0, 0, 0.05)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              boxShadow: isOwner
                                ? "0 4px 12px rgba(74, 144, 226, 0.3)"
                                : "0 2px 8px rgba(0, 0, 0, 0.08)",
                            },
                            "&:hover .menu-button": {
                              opacity: 1,
                            },
                          }}
                        >
                          {isEditing ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={4}
                                value={editCommentContent}
                                onChange={(e) => setEditCommentContent(e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1.5,
                                    fontSize: "0.875rem",
                                    bgcolor: isOwner ? "rgba(255, 255, 255, 0.15)" : "#F8FBFF",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: isOwner ? "rgba(255, 255, 255, 0.3)" : "#E1ECFA",
                                    },
                                  },
                                  "& .MuiInputBase-input": {
                                    color: isOwner ? "#FFFFFF" : "#2C4A68",
                                  },
                                }}
                              />
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75 }}>
                                <Button
                                  size="small"
                                  onClick={handleCancelEdit}
                                  disabled={isUpdatingComment}
                                  sx={{
                                    textTransform: "none",
                                    color: isOwner ? "#FFFFFF" : "#8FA5C0",
                                    minWidth: "auto",
                                    px: 1.5,
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSaveEdit(comment)}
                                  disabled={isUpdatingComment || !editCommentContent.trim()}
                                  sx={{
                                    textTransform: "none",
                                    bgcolor: isOwner ? "#FFFFFF" : "#4A90E2",
                                    color: isOwner ? "#4A90E2" : "#FFFFFF",
                                    minWidth: "auto",
                                    px: 1.5,
                                    "&:hover": {
                                      bgcolor: isOwner ? "#F0F0F0" : "#357ABD",
                                    },
                                  }}
                                >
                                  {isUpdatingComment ? "Saving..." : "Save"}
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <>
                              {/* Menu Button - Only for owner */}
                              {isOwner && (
                                <IconButton
                                  className="menu-button"
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, comment)}
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    opacity: 0,
                                    transition: "opacity 0.2s ease",
                                    color: isOwner ? "rgba(255, 255, 255, 0.8)" : "#8FA5C0",
                                    "&:hover": {
                                      bgcolor: isOwner
                                        ? "rgba(255, 255, 255, 0.15)"
                                        : "rgba(0, 0, 0, 0.05)",
                                      color: isOwner ? "#FFFFFF" : "#2C4A68",
                                    },
                                  }}
                                >
                                  <MoreVertIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}

                              {/* Message Content */}
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  fontSize: "0.875rem",
                                  lineHeight: 1.6,
                                  pr: isOwner ? 3 : 0,
                                  color: isOwner ? "#FFFFFF" : "#2C4A68",
                                }}
                              >
                                {comment.content}
                              </Typography>

                              {/* Mentions - More subtle */}
                              {comment.mentions && comment.mentions.length > 0 && (
                                <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${isOwner ? "rgba(255, 255, 255, 0.2)" : "#E8EBF0"}` }}>
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                                    {comment.mentions.map((mention, idx) => (
                                      <Chip
                                        key={`${mention.mentioned_person_id}-${idx}`}
                                        label={`@${mention.name || mention.email || "Unknown"}`}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: "0.65rem",
                                          bgcolor: isOwner
                                            ? "rgba(255, 255, 255, 0.2)"
                                            : mention.user_type === "admin"
                                            ? "#EBF4FF"
                                            : "#FFF4E6",
                                          color: isOwner
                                            ? "#FFFFFF"
                                            : mention.user_type === "admin"
                                            ? "#4A90E2"
                                            : "#F57C00",
                                          border: "none",
                                          "& .MuiChip-label": { px: 0.75 },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {/* Attachments */}
                              <CommentAttachments attachments={comment.attachments} />
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Fade>
                );
              })}
              <div ref={commentsEndRef} />
            </Stack>
          )}
        </Box>

        {/* ─── Input ─── */}
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 2.5,
            bgcolor: "#FFFFFF",
            borderTop: "1px solid #E8EBF0",
            boxShadow: "0 -2px 12px rgba(0, 0, 0, 0.02)",
          }}
        >
          {/* Selected Attachments Preview */}
          {selectedAttachments.length > 0 && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                bgcolor: "#F8FBFF",
                borderRadius: 2,
                border: "1px solid #E1ECFA",
              }}
            >
              <Typography variant="caption" sx={{ color: "#8FA5C0", mb: 1, display: "block" }}>
                Attachments ({selectedAttachments.length})
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {selectedAttachments.map((attachment, index) => (
                  <Chip
                    key={attachment.id || index}
                    label={attachment.metadata?.originalName || `Attachment ${index + 1}`}
                    onDelete={() => {
                      setSelectedAttachments((prev) => prev.filter((_, i) => i !== index));
                    }}
                    size="small"
                    sx={{
                      bgcolor: "#FFFFFF",
                      border: "1px solid #E1ECFA",
                      "& .MuiChip-deleteIcon": {
                        color: "#8FA5C0",
                        "&:hover": {
                          color: "#C71414",
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Text input + send button */}
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-end", position: "relative" }}>
            {/* Add Attachment Button */}
            <Tooltip title="Attach file" placement="top" arrow>
              <IconButton
                onClick={() => attachmentFileInputRef.current?.click()}
                disabled={uploadingAttachments}
                size="small"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  background: "#F8FBFF",
                  color: "#4A90E2",
                  border: "1px solid #E1ECFA",
                  flexShrink: 0,
                  transition: "all 0.25s ease",
                  "&:hover": {
                    background: "#EBF4FF",
                    borderColor: "#C7DCED",
                    transform: "scale(1.05)",
                  },
                  "&.Mui-disabled": {
                    background: "#F0F0F0",
                    color: "#B8CAD9",
                    borderColor: "#E0E0E0",
                  },
                }}
              >
                {uploadingAttachments ? (
                  <CircularProgress size={18} color="inherit" thickness={4} />
                ) : (
                  <AttachFileIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>
            </Tooltip>

            <Box ref={textInputRef} sx={{ flex: 1, position: "relative" }}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                placeholder={selectedAttachments.length > 0 ? "Add a message..." : "Type your message...Use @ to mention"}
                value={newComment}
                onChange={handleCommentChange}
                onKeyDown={handleCommentKeyDown}
                variant="outlined"
                size="small"
                sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  fontSize: "0.875rem",
                  bgcolor: "#F8FBFF",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    bgcolor: "#EBF4FF",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#C7DCED",
                    },
                  },
                  "&.Mui-focused": {
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 0 0 3px rgba(74, 144, 226, 0.1)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4A90E2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E1ECFA",
                  },
                },
                "& .MuiInputBase-input": {
                  color: "#2C4A68",
                  padding: "2px !important",
                  "&::placeholder": {
                    color: "#8FA5C0",
                    opacity: 0.8,
                  },
                },
              }}
            />
            </Box>
            <Tooltip title="Send (Enter)" placement="top" arrow>
              <span>
                <IconButton
                  onClick={handleSubmitComment}
                  disabled={(!newComment.trim() && selectedAttachments.length === 0) || loading}
                  size="small"
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    background: (newComment.trim() || selectedAttachments.length > 0)
                      ? "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)"
                      : "#E8EBF0",
                    color: (newComment.trim() || selectedAttachments.length > 0) ? "#fff" : "#B8CAD9",
                    flexShrink: 0,
                    transition: "all 0.25s ease",
                    boxShadow: newComment.trim()
                      ? "0 4px 12px rgba(74, 144, 226, 0.3)"
                      : "none",
                    "&:hover": {
                      background: (newComment.trim() || selectedAttachments.length > 0)
                        ? "linear-gradient(135deg, #357ABD 0%, #2868AB 100%)"
                        : "#D4DFE9",
                      transform: (newComment.trim() || selectedAttachments.length > 0) ? "translateY(-2px)" : "none",
                      boxShadow: (newComment.trim() || selectedAttachments.length > 0)
                        ? "0 6px 16px rgba(74, 144, 226, 0.4)"
                        : "none",
                    },
                    "&.Mui-disabled": {
                      color: "#B8CAD9",
                      background: "#E8EBF0",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={18} color="inherit" thickness={4} />
                  ) : (
                    <SendIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              mt: 1,
              display: "block",
              color: "#8FA5C0",
              textAlign: "center",
            }}
          >
            Press <strong>Enter</strong> to send · <strong>Shift + Enter</strong> for new line · Use <strong>@</strong> to mention
          </Typography>
          
          {/* Mention Popover */}
          <Popover
            open={Boolean(mentionAnchor)}
            anchorEl={mentionAnchor}
            onClose={closeMentionPopover}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            disableAutoFocus
            disableEnforceFocus
            PaperProps={{
              sx: {
                maxHeight: 300,
                width: mentionAnchor ? (mentionAnchor.offsetWidth || 320) : 320,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                borderRadius: 2,
                mb: 0.5,
              },
            }}
          >
            <List dense sx={{ p: 0 }}>
              {filteredMentions.length === 0 ? (
                <ListItemButton disabled>
                  <ListItemText
                    primary="No users found"
                    primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                  />
                </ListItemButton>
              ) : (
                filteredMentions.map((mention, index) => (
                  <ListItemButton
                    key={mention.id}
                    selected={index === mentionSelectedIndex}
                    onClick={() => selectMention(mention)}
                    sx={{
                      py: 1,
                      "&.Mui-selected": {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                        },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.75rem",
                          bgcolor: mention.type === "admin" ? "#4A90E2" : "#F57C00",
                        }}
                      >
                        {mention.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={mention.name}
                      secondary={mention.email}
                      primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                      secondaryTypographyProps={{ 
                        variant: "body2", 
                        sx: { textTransform: "none",
                          color: theme.customColors.text.secondary,
                          fontSize: "0.775rem",
                         } 
                      }}
                    />
                    <Chip
                      label={mention.type === "admin" ? "Admin" : "Auditor"}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        bgcolor: mention.type === "admin" ? "#EBF4FF" : "#FFF4E6",
                        color: mention.type === "admin" ? "#4A90E2" : "#F57C00",
                        ml: 1,
                      }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          </Popover>

          {/* Menu for Edit/Delete */}
          <Popover
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                minWidth: 120,
              },
            }}
          >
            <List dense sx={{ p: 0.5 }}>
              <ListItemButton
                onClick={() => handleEditClick(selectedCommentForMenu)}
                sx={{
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 18, mr: 1.5, color: "#4A90E2" }} />
                <ListItemText
                  primary="Edit"
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
              <ListItemButton
                onClick={() => handleOpenDeleteDialog(selectedCommentForMenu)}
                sx={{
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha("#C71414", 0.08),
                  },
                }}
              >
                <DeleteIcon sx={{ fontSize: 18, mr: 1.5, color: "#C71414" }} />
                <ListItemText
                  primary="Delete"
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 500,
                    color: "#C71414",
                  }}
                />
              </ListItemButton>
            </List>
          </Popover>

          {/* Hidden File Input */}
          <input
            type="file"
            ref={attachmentFileInputRef}
            style={{ display: "none" }}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm,.mkv"
            multiple
            onChange={handleFileSelection}
          />
        </Box>
      </Box>
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        heading="Delete Comment"
        content="Are you sure you want to delete this comment? This action cannot be undone."
        buttonText={isDeletingComment ? "Deleting..." : "Delete"}
        loading={isDeletingComment}
      />
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Drawer>
  );
};

export default ControlCommentsDrawer;